/**
 * Route preloading utilities for performance optimization
 */

/**
 * Preload critical routes that are likely to be accessed immediately
 * This improves perceived performance by loading components in the background
 */
export function preloadCriticalRoutes(): void {
  // Note: LoginPage is loaded statically in App.vue, no need to preload

  // Preload home pages as users will navigate there after login
  import('@/features/homepage/views/HomePageStudent.vue');
  import('@/features/homepage/views/EmployeeHomePage.vue');
}

/**
 * Preload routes based on user role
 * Call this after determining the user's role to preload relevant components
 */
export function preloadRoleBasedRoutes(role: 'student' | 'employee'): void {
  if (role === 'student') {
    // Preload student-specific routes
    import('@/features/absences/views/AbsencesPage.vue');
    import('@/features/agenda/views/AgendaView.vue');
  } else if (role === 'employee') {
    // Preload employee-specific routes
    import('@/features/rooms/views/PlanningPage.vue');
    import('@/features/rooms/views/DisponibilitiesPage.vue');
  }
}

/**
 * Preload a specific route component
 * Useful for hover preloading or predictive loading
 */
export function preloadRoute(routePath: string): Promise<any> | null {
  const routeMap: Record<string, () => Promise<any>> = {
    // Note: LoginPage removed as it's statically loaded in App.vue
    '/student-home': () => import('@/features/homepage/views/HomePageStudent.vue'),
    '/employee-home': () => import('@/features/homepage/views/EmployeeHomePage.vue'),
    '/absences': () => import('@/features/absences/views/AbsencesPage.vue'),
    '/agenda': () => import('@/features/agenda/views/AgendaView.vue'),
    '/rooms/planning': () => import('@/features/rooms/views/PlanningPage.vue'),
    '/rooms/disponibilities': () => import('@/features/rooms/views/DisponibilitiesPage.vue'),
    '/settings': () => import('@/features/settings/views/SettingsPage.vue'),
  };

  const preloader = routeMap[routePath];
  return preloader ? preloader() : null;
}
